import React from "react";
import { CalendarDays, Database, Scale, ShieldAlert, ArrowRight } from "lucide-react";
import { Link } from "react-router-dom";
import { EvidenceStatus } from "./EvidenceStatus";
import { LAST_CONTENT_UPDATE, METHODOLOGY_VERSION, currentEvidenceNotice } from "../data/research";
import { criterionEvidence, sourceCatalog } from "../data/evidence";

const researchFacts = [
  { icon: Database, value: criterionEvidence.length, label: "criterion evidence records" },
  { icon: Scale, value: Object.keys(sourceCatalog).length, label: "cited first-party sources" },
  { icon: CalendarDays, value: LAST_CONTENT_UPDATE, label: "content update" },
];

const Hero = () => (
  <section className="border-b border-slate-200 bg-[#FAF9F6]" data-testid="research-hero">
    <div className="mx-auto max-w-[1280px] px-4 pb-14 pt-12 sm:px-6 lg:px-8 lg:pb-20 lg:pt-16">
      <div className="grid gap-10 lg:grid-cols-[1.25fr_0.75fr] lg:items-end">
        <div>
          <div className="mb-5 flex flex-wrap items-center gap-3">
            <span className="border border-slate-300 bg-white px-2.5 py-1 font-mono text-xs font-semibold uppercase tracking-widest text-slate-600">Methodology {METHODOLOGY_VERSION}</span>
            <EvidenceStatus label={currentEvidenceNotice.status} tone="disclosed" detail={currentEvidenceNotice.detail} testId="hero-evidence-status" />
          </div>
          <h1 className="max-w-4xl font-serif text-4xl font-bold leading-[1.05] text-slate-950 sm:text-5xl lg:text-6xl" data-testid="homepage-main-heading">
            E-Com Benchmark Lab
          </h1>
          <p className="mt-5 max-w-3xl text-lg leading-relaxed text-slate-700">
            Transparent ecommerce platform research and criteria-led comparisons, with visible methodology, evidence status, limitations and commercial disclosures.
          </p>
          <div className="mt-8 flex flex-wrap gap-3">
            <Link to="/methodology" data-testid="hero-methodology-link" className="inline-flex items-center gap-2 bg-[#0F2942] px-5 py-3 text-sm font-semibold text-white transition-colors hover:bg-slate-900 focus:outline-none focus:ring-2 focus:ring-blue-600 focus:ring-offset-2">
              Review the methodology <ArrowRight className="h-4 w-4" />
            </Link>
            <Link to="/disclosure" data-testid="hero-disclosure-link" className="inline-flex items-center gap-2 border border-slate-300 bg-white px-5 py-3 text-sm font-semibold text-slate-800 transition-colors hover:border-slate-500 focus:outline-none focus:ring-2 focus:ring-blue-600 focus:ring-offset-2">
              Read commercial disclosure
            </Link>
          </div>
        </div>

        <aside className="border border-slate-300 bg-white" data-testid="research-scope-panel">
          <div className="border-b border-slate-200 px-5 py-4">
            <p className="font-mono text-xs font-semibold uppercase tracking-widest text-slate-500">Current research scope</p>
          </div>
          <dl className="divide-y divide-slate-200">
            {researchFacts.map(({ icon: Icon, value, label }) => (
              <div key={label} className="grid grid-cols-[32px_1fr] gap-3 px-5 py-4">
                <Icon className="mt-0.5 h-5 w-5 text-blue-700" aria-hidden="true" />
                <div><dt className="font-mono text-sm font-bold text-slate-950">{value}</dt><dd className="text-xs text-slate-500">{label}</dd></div>
              </div>
            ))}
          </dl>
          <div className="flex gap-3 border-t border-amber-200 bg-amber-50 px-5 py-4" data-testid="hero-commercial-disclosure">
            <ShieldAlert className="mt-0.5 h-5 w-5 shrink-0 text-amber-700" />
            <p className="text-xs leading-relaxed text-amber-950">Published by the creators of Simplestores. Numeric product scores are withheld; cited vendor material is not represented as independent testing.</p>
          </div>
        </aside>
      </div>
    </div>
  </section>
);

export default Hero;